import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { eventsService } from '../../services/eventsService';
import VendorPlatformPill from '../Shared/VendorPlatformPill';
import DateRangePicker from '../Shared/DateRangePicker';

export const localDateKey = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
};

const formatOverviewMoney = (value, currency = 'USD') => {
  if (value == null || !Number.isFinite(Number(value))) return 'Unavailable';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(Number(value));
};

const addUnique = (list, value) => {
  if (value && !list.includes(value)) list.push(value);
};

// One row per event + ticket type. VOID rows only when listed or availability is not zero.
export const groupEventRows = (items, statsById = {}) => {
  const groups = new Map();
  items.forEach((item) => {
    const ref = item.event_ref || item.source_uuid || item.event_id;
    const key = `${ref}|${String(item.ticket_type || '').trim().toLowerCase()}`;
    const stats = statsById[item.event_id] || {};
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        ref,
        item,
        availability: 0,
        startingInventory: 0,
        toTransfer: 0,
        listed: 0,
        soldOut: true,
        vendors: [],
        users: [],
      });
    }
    const group = groups.get(key);
    group.availability += Number(item.availability || 0);
    group.startingInventory += Number(stats.starting_inventory || 0);
    group.toTransfer += Number(stats.to_transfer || 0);
    group.listed = Math.max(group.listed, Number(stats.listed || 0));
    group.soldOut = group.soldOut && Boolean(item.sold_out);
    addUnique(group.vendors, item.vendor);
    addUnique(group.users, item.username || item.assigned_user_raw);
  });
  return [...groups.values()].filter((group) => (
    String(group.item.ticket_type || '').trim().toUpperCase() !== 'VOID'
    || group.listed > 0
    || group.availability !== 0
  ));
};

const DashboardEventsWidget = ({ showOverview = false }) => {
  const [dateRange, setDateRange] = useState(() => {
    const today = localDateKey(new Date());
    return { start: today, end: today };
  });
  const selectedDate = dateRange.start;
  const selectedEndDate = dateRange.end || dateRange.start;
  const [items, setItems] = useState([]);
  const [statsById, setStatsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(showOverview);
  const [overviewError, setOverviewError] = useState('');

  useEffect(() => {
    let active = true;

    if (!selectedDate) {
      setItems([]);
      setError('Select an event date.');
      setLoading(false);
      return () => {
        active = false;
      };
    }

    const loadEvents = async () => {
      setLoading(true);
      setError('');
      const [result, statsResult] = await Promise.all([
        eventsService.getDisplay({
          startDate: selectedDate,
          endDate: selectedEndDate,
          limit: 3000,
        }),
        eventsService.getDashboardStats(selectedDate, selectedEndDate),
      ]);
      if (!active) return;

      if (result.success) {
        setItems(result.items || []);
      } else {
        setItems([]);
        setError(result.error || 'Failed to load events.');
      }
      if (statsResult?.success) {
        setStatsById(Object.fromEntries((statsResult.items || []).map((stat) => [stat.event_id, stat])));
      } else {
        setStatsById({});
        if (result.success) setError(statsResult?.error || 'Failed to load event stats.');
      }
      setLoading(false);
    };

    loadEvents();
    return () => {
      active = false;
    };
  }, [selectedDate, selectedEndDate]);

  useEffect(() => {
    let active = true;

    if (!showOverview || !selectedDate) {
      setOverview(null);
      setOverviewError('');
      setOverviewLoading(false);
      return () => {
        active = false;
      };
    }

    const loadOverview = async () => {
      setOverviewLoading(true);
      setOverviewError('');
      const result = await eventsService.getDashboardOverview(selectedDate, selectedEndDate);
      if (!active) return;

      if (result.success) {
        setOverview(result);
      } else {
        setOverview(null);
        setOverviewError(result.error || 'Failed to load the Overview.');
      }
      setOverviewLoading(false);
    };

    loadOverview();
    return () => {
      active = false;
    };
  }, [selectedDate, selectedEndDate, showOverview]);

  const eventCount = useMemo(() => new Set(
    items.map((item) => item.event_ref || item.source_uuid || item.event_id).filter(Boolean),
  ).size, [items]);
  const rows = useMemo(() => groupEventRows(items, statsById), [items, statsById]);
  const overviewProfitTone = overview?.profit == null
    ? ''
    : Number(overview.profit) >= 0 ? 'is-positive' : 'is-negative';

  return (
    <section className="dashboard-events-section">
      <div className="dashboard-events-header">
        <div>
          <h2>Events</h2>
          <span className="section-subtitle">
            {loading ? 'Loading selected date…' : `${eventCount} events · ${items.length} positions`}
          </span>
        </div>
        <div className="dashboard-events-controls">
          <label htmlFor="dashboard-events-date">Event date</label>
          <DateRangePicker id="dashboard-events-date" value={dateRange} onChange={setDateRange} />
          <Link className="dashboard-events-open-link" to="/user/events">Open Events</Link>
        </div>
      </div>

      {showOverview && (
        <div className="dashboard-personal-overview" aria-label="Event overview">
          <div className="dashboard-personal-overview-heading">
            <div>
              <span className="dashboard-personal-overview-eyebrow">Overview</span>
              <h3>All movement for the selected date</h3>
            </div>
            {overview?.currency_issues?.length > 0 && (
              <span
                className="dashboard-personal-overview-warning"
                title={overview.currency_issues.join('\n')}
              >
                Currency review needed
              </span>
            )}
          </div>

          {overviewError ? (
            <div className="notification error dashboard-events-message">{overviewError}</div>
          ) : overviewLoading ? (
            <div className="dashboard-personal-overview-loading">Loading totals...</div>
          ) : overview && (
            <div className="dashboard-personal-overview-grid">
              <article className={`dashboard-overview-metric ${overviewProfitTone}`.trim()}>
                <span>Profit</span>
                <strong>{formatOverviewMoney(overview.profit, overview.currency)}</strong>
              </article>
              <article className="dashboard-overview-metric">
                <span>Revenue</span>
                <strong>{formatOverviewMoney(overview.revenue, overview.currency)}</strong>
              </article>
              <article className="dashboard-overview-metric">
                <span>Cost</span>
                <strong>{formatOverviewMoney(overview.cost, overview.currency)}</strong>
              </article>
              <article className="dashboard-overview-metric is-tickets">
                <span>Tickets to Sell</span>
                <strong>{overview.tickets_to_sell ?? 0}</strong>
              </article>
              <article className="dashboard-overview-metric is-transfer">
                <span>Tickets to Transfer</span>
                <strong>{overview.tickets_to_transfer ?? 0}</strong>
              </article>
            </div>
          )}
        </div>
      )}

      {error && <div className="notification error dashboard-events-message">{error}</div>}
      {loading ? (
        <div className="dashboard-events-loading">
          <div className="loading-spinner"></div>
          <span>Loading events…</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="dashboard-events-empty">No events found for this date.</div>
      ) : (
        <div className="dashboard-events-table-wrap">
          <table className="dashboard-events-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Event</th>
                <th>Ticket Type</th>
                <th>Venue</th>
                <th>Vendor</th>
                <th>Starting Inventory</th>
                <th>Availability</th>
                <th>Pending Transfer</th>
                <th>Sold Out</th>
                <th>User</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((group) => {
                const { item } = group;
                const eventName = item.event_name || '';
                const artist = item.artist || '';
                const showEventName = eventName && eventName.trim().toLowerCase() !== artist.trim().toLowerCase();
                return (
                  <tr key={group.key}>
                    <td>
                      <Link to={`/user/events?q=${encodeURIComponent(group.ref || '')}`}>
                        {group.ref || '-'}
                      </Link>
                    </td>
                    <td className="dashboard-event-name-cell">
                      <strong>{artist || eventName || '-'}</strong>
                      {showEventName && <span>{eventName}</span>}
                    </td>
                    <td>{item.ticket_type || '-'}</td>
                    <td>{item.venue || '-'}</td>
                    <td><VendorPlatformPill kind="vendor" value={group.vendors.join(' / ')} /></td>                    <td className="dashboard-event-availability">{group.startingInventory}</td>
                    <td className="dashboard-event-availability">{group.availability}</td>
                    <td className="dashboard-event-availability">{group.toTransfer}</td>
                    <td>
                      <span className={`dashboard-sold-out-pill ${group.soldOut ? 'is-sold-out' : 'is-available'}`}>
                        {group.soldOut ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>{group.users.join(', ') || 'Unassigned'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default DashboardEventsWidget;
